import './notificationInfo.scss';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Loader from 'components/loader';
import Button from 'components/button';
import FormDisplayGroup from 'components/formDisplayGroup';
import NotificationStatusBadge from 'components/notificationStatusBadge';
import { useNotificationQuery } from 'hooks/queries/use-notification.queries';
import {
	NOTIFICATIONS_KEY,
	NOTIFICATION_RECIPIENTS_KEY,
} from 'constants/TanStackKeys';
import {
	ALL_MEMBERS_LABEL,
	AUDIENCE_TYPES,
	formatDateTime,
	getNotificationPollingInterval,
} from 'helpers/notificationDelivery';
import { DeliverySummary } from './deliverySummary';
import { RecipientsTable } from './recipientsTable';

export const NotificationInfo = ({
	notificationId,
	setAction,
	onRequestClose,
	...rest
}) => {
	const queryClient = useQueryClient();
	const {
		data: notification,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useNotificationQuery(notificationId);

	const onRefresh = () => {
		refetch();
		queryClient.invalidateQueries({
			queryKey: [NOTIFICATION_RECIPIENTS_KEY, notificationId],
		});
	};

	// Polling stops with the detail's last response, which can be newer than
	// the last recipients page: refresh the table, and the list summary, once
	// more when the send settles.
	const isPolling = !!getNotificationPollingInterval(notification);
	const wasPolling = useRef(false);
	useEffect(() => {
		if (wasPolling.current && !isPolling) {
			queryClient.invalidateQueries({
				queryKey: [NOTIFICATION_RECIPIENTS_KEY, notificationId],
			});
			queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
		}
		wasPolling.current = isPolling;
	}, [isPolling, notificationId, queryClient]);

	if (isLoading) {
		return (
			<div className="NotificationInfo" {...rest}>
				<Loader />
			</div>
		);
	}

	if (error || notification?.error) {
		return (
			<div className="NotificationInfo" {...rest}>
				<div className="NotificationInfo__form">
					<div className="NotificationInfo__form__section">
						<p>Error loading notification details</p>
					</div>
				</div>
			</div>
		);
	}

	const venues = (notification?.audience || []).filter(Boolean);
	const isAllMembers = notification?.audience_type === AUDIENCE_TYPES.ALL;

	return (
		<div className="NotificationInfo" {...rest}>
			<div className="NotificationInfo__form">
				<div className="NotificationInfo__form__section">
					<div className="NotificationInfo__header">
						<div className="NotificationInfo__header__status">
							<NotificationStatusBadge status={notification?.status} />
							{isPolling && (
								<span className="NotificationInfo__header__hint">
									Updating automatically
								</span>
							)}
						</div>
						<Button
							buttonWrapperClass="NotificationInfo__refresh"
							displayLabel={isFetching ? 'Refreshing…' : 'Refresh'}
							type="button"
							disabled={isFetching}
							onClick={onRefresh}
						/>
					</div>
					<FormDisplayGroup
						fieldLabel="Title"
						fieldValue={notification?.title}
					/>
					<FormDisplayGroup
						fieldLabel="Message"
						fieldValue={notification?.message}
					/>
					{isAllMembers || venues.length === 0 ? (
						<FormDisplayGroup
							fieldLabel="Audience"
							fieldValue={isAllMembers ? ALL_MEMBERS_LABEL : null}
						/>
					) : (
						<FormDisplayGroup fieldLabel="Audience" fieldValues={venues} />
					)}
					<FormDisplayGroup
						fieldLabel="Created By"
						fieldValue={notification?.admin?.full_name}
					/>
					<FormDisplayGroup
						fieldLabel="Sent"
						fieldValue={formatDateTime(notification?.sent_at_iso)}
					/>
					<FormDisplayGroup
						fieldLabel="Total Recipients"
						fieldValue={
							notification?.total_recipients > 0
								? notification.total_recipients.toLocaleString()
								: null
						}
					/>
				</div>
				<div className="NotificationInfo__form__section NotificationInfo__delivery">
					<DeliverySummary notification={notification} isPolling={isPolling} />
					<RecipientsTable
						notificationId={notificationId}
						notification={notification}
					/>
				</div>
			</div>
		</div>
	);
};
